import { FC, useMemo } from 'react'
import { FutureLayerUID, FutureLayer } from 'components/features/future/types'
import { FuturePanePreset } from 'components/features/future/FuturePanePreset/FuturePanePreset'
import * as yup from 'yup'
import { useLayerSelector, extendCommonSchema } from 'components/features/future/helpers'
import { FutureWhenInput } from 'components/features/future/FutureWhenInput/FutureWhenInput'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { maskNumberFromZeroTo1200 } from 'components/atoms/inputs/maskedInput'
import { schemaNumber } from 'schemas/baseSchema'
import { useCollectLayerErrors } from 'components/features/future/hooks/useCollectErrors'
import { ObjectSchema } from 'yup'
import { LayerStatus } from 'components/features/future/LayerStatus/LayerStatus'

const LAYER_SLUG = 'lose_job' as const
type LayerSlugType = typeof LAYER_SLUG
type LayerType = Extract<FutureLayer, { slug: LayerSlugType }>

const schemaCreator = extendCommonSchema<LayerSlugType>(yup.object({
  duration_months: schemaNumber,
}) as ObjectSchema<LayerType>)

type FutureLayerPaneLoseJobProps = {
  uid?: FutureLayerUID
  onCancel: () => void
}

export const FutureLayerPaneLoseJob: FC<FutureLayerPaneLoseJobProps> = (props) => {
  const { onCancel, uid } = props
  const layer = useLayerSelector<LayerSlugType>(props)

  const layerWithDefaults = useMemo(() => ({
    duration_months: 1,
    ...layer,
  } as typeof layer), [layer])

  return (
    <FuturePanePreset<LayerSlugType>
      layer={ layerWithDefaults }
      slug={ LAYER_SLUG }
      name='Lose Your Job'
      icon='income_loss'
      type='event'
      schemaCreator={ schemaCreator }
      onCancel={ onCancel }
    >
      <FormFields uid={ uid } />
    </FuturePanePreset>
  )
}

type FormFieldsProps = {
  uid?: FutureLayerUID
}
const FormFields: FC<FormFieldsProps> = (props) => {
  const { uid } = props

  const errors = useCollectLayerErrors<LayerType>()

  const completeChild = useMemo(() => (
    <>
      <h1>
        Event Completed
      </h1>
      Remember to update your Settings and Plan:
      <ul>
        <li>Update your current income in Settings</li>
        <li>Add an Income Change event to your Plan to reflect your expected future income</li>
      </ul>
    </>
  ), [])

  return (
    <LayerStatus uid={ uid || null } skipPartialState completeContent={ completeChild } type='event'>
      <FutureWhenInput error={ errors.whenField } />

      <FutureFieldSlider
        label='For how long (months)'
        name='duration_months'
        mask={ maskNumberFromZeroTo1200 }

        error={ errors.duration_months }
        note='How many months you expect to be out of work'
        min={ 1 }
        max={ 96 }
        step={ 1 }
      />

    </LayerStatus>
  )
}
